/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useDirectDialList.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Direct Dial list.
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { fetchDirectDials, type ListParams } from '../api/directDialApi';

interface UseDirectDialListOptions {
  /** Pre-configured columns; if empty, caller builds them in the Page component */
  initialColumns?: { key: string; label: string; visible: boolean; sortable: boolean; render?: (value: unknown, item: unknown) => React.ReactNode }[];
  initialSortBy?: string;
  initialSortOrder?: 'asc' | 'desc';
  initialLimit?: number;
  domainId?: number;
}

export function useDirectDialList(options: UseDirectDialListOptions = {}) {
  const {
    initialColumns = [],
    initialSortBy = 'code',
    initialSortOrder = 'asc',
    initialLimit = 10,
    domainId
  } = options;

  const sortableColumns = new Set([
    'code',
    'name',
    'destinationModule',
    'destinationValue',
    'enabled',
    'createdAt',
    'updatedAt'
  ]);

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }) => {
    const safeSortBy = params.sortBy && sortableColumns.has(params.sortBy)
      ? params.sortBy
      : initialSortBy;
    const safeSortOrder = params.sortOrder === 'desc' ? 'desc' : 'asc';

    const response = await fetchDirectDials({
      page: params.page,
      limit: params.limit,
      sortBy: safeSortBy,
      sortOrder: safeSortOrder,
      search: params.search?.trim(),
      domainId,
    });
    return {
      data: response.data || [],
      pagination: response.pagination || {
        page: params.page,
        limit: params.limit,
        total: 0,
        totalPages: 0
      }
    } as DataGridFetchResult;
  }, [sortableColumns, initialSortBy, domainId]);

  const dataGrid = useDataGrid({
    fetchData,
    initialColumns,
    initialSortBy,
    initialSortOrder,
    initialLimit,
  });

  // Refresh data when domainId changes
  useEffect(() => {
    if (domainId !== undefined) {
      dataGrid.refresh();
    }
  }, [domainId]);

  return dataGrid;
}

